/**
 * How the generic agent report page (spec §5) reviews each output kind, and
 * the tabs a type adds of its own. An output kind with an entry here gets a
 * "waiting for a person" list on every agent that produces it; a type's extra
 * tabs show only on agents of that type.
 */

import type { ComponentType } from 'react';
import type { AgentBehaviorKind, AgentOutputKind } from '../../../lib/ai/agentDefinitions';
import type { OutputReviewProps } from './types';
import { ValueProposalsReview } from './ValueProposalsReview';
import { EstimateAccuracyTab } from './EstimateAccuracyTab';
import { useOpenValueProposals } from './useOpenValueProposals';
import { ClassificationProposalsReview } from './ClassificationProposalsReview';
import { useOpenClassificationProposals, useOpenClassificationStore } from './useOpenClassificationProposals';
import { useValueProposalStore } from '../../../stores/prioritization/valueProposalStore';
import { ClusterProposalsReview } from './ClusterProposalsReview';
import { useOpenClusterProposals } from './useOpenClusterProposals';
import { useTwinsStore } from '../../../stores/prioritization/twinsStore';
import { RelationshipProposalsReview } from './RelationshipProposalsReview';
import { useOpenRelationshipProposals } from './useOpenRelationshipProposals';
import { useNexusRelationshipStore } from '../../../stores/prioritization/nexusRelationshipStore';
import { FindingsReview } from './FindingsReview';
import { useOpenFindings } from './useOpenFindings';
import { useAgentFindingsStore } from '../../../stores/prioritization/agentFindingsStore';

export interface OutputReviewer {
  /** The tab's name: what waits for a person. */
  label: string;
  Component: ComponentType<OutputReviewProps>;
  /** How many are waiting, for the tab label. */
  useCount: (agentType: string) => number;
  /** Re-read what waits from the database (the page's Refresh, and after a run). */
  refresh: (workspaceId: string, agentType: string) => Promise<void>;
}

export const OUTPUT_REVIEWERS: Partial<Record<AgentOutputKind, OutputReviewer>> = {
  'proposal:value': {
    label: 'Proposed values',
    Component: ValueProposalsReview,
    useCount: (agentType) => useOpenValueProposals(agentType).length,
    refresh: () => useValueProposalStore.getState().fetchFromSupabase(),
  },
  'proposal:classification': {
    label: 'Proposed classifications',
    Component: ClassificationProposalsReview,
    useCount: (agentType) => useOpenClassificationProposals(agentType).length,
    refresh: (workspaceId, agentType) => useOpenClassificationStore.getState().load(workspaceId, agentType),
  },
  'proposal:cluster': {
    label: 'Possible duplicates',
    Component: ClusterProposalsReview,
    useCount: (agentType) => useOpenClusterProposals(agentType).length,
    refresh: (workspaceId) => useTwinsStore.getState().fetchClusters(workspaceId),
  },
  'proposal:relationship': {
    label: 'Proposed relationships',
    Component: RelationshipProposalsReview,
    useCount: (agentType) => useOpenRelationshipProposals(agentType).length,
    refresh: (workspaceId) => useNexusRelationshipStore.getState().fetchProposals(workspaceId),
  },
  finding: {
    label: 'Findings',
    Component: FindingsReview,
    useCount: (agentType) => useOpenFindings(agentType).length,
    refresh: (workspaceId, agentType) => useAgentFindingsStore.getState().fetchActive(workspaceId, agentType),
  },
};

export interface BehaviorReportTab {
  key: string;
  label: string;
  Component: ComponentType<Pick<OutputReviewProps, 'workspaceId' | 'titleOf'>>;
}

export const BEHAVIOR_REPORT_TABS: Record<AgentBehaviorKind, BehaviorReportTab[]> = {
  estimate_value: [{ key: 'accuracy', label: 'Accuracy', Component: EstimateAccuracyTab }],
  classify: [],
  detect_cluster: [],
  propose_relationship: [],
  draft_items: [],
  find_issues: [],
};

export { ValueProposalsReview };
export type { OutputReviewProps } from './types';
